import React from "react";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from "react-router-dom";

// Colorful rep workload cards showing facilities by status: active / in-progress / inactive
const PALETTES = [
  { grad: "from-indigo-600 to-indigo-500",  ring: "ring-indigo-100" },
  { grad: "from-emerald-600 to-emerald-500", ring: "ring-emerald-100" },
  { grad: "from-orange-500 to-amber-500",   ring: "ring-orange-100" },
  { grad: "from-sky-600 to-sky-500",        ring: "ring-sky-100" },
  { grad: "from-violet-600 to-violet-500",  ring: "ring-violet-100" },
  { grad: "from-rose-600 to-rose-500",      ring: "ring-rose-100" },
  { grad: "from-slate-800 to-slate-700",    ring: "ring-slate-100" },
];

function RepTile({ rep, index }) {
  const palette = PALETTES[index % PALETTES.length];
  const initials = (rep.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const total = rep.counts.total || 0;
  return (
    <Link
      to={rep.user_id ? `/facilities?rep=${rep.user_id}` : "/facilities"}
      className={`block rounded-lg p-4 text-white bg-gradient-to-br ${palette.grad} ring-4 ${palette.ring} transition-transform duration-200 hover:-translate-y-[3px]`}
      data-testid={`rep-tile-${rep.user_id || "unassigned"}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <Avatar className="w-10 h-10 border-2 border-white/40">
          {rep.picture ? <AvatarImage src={rep.picture} /> : null}
          <AvatarFallback className="bg-white/20 text-white text-sm font-medium">{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="font-heading font-bold text-sm truncate">{rep.name}</div>
          <div className="text-[10px] uppercase tracking-widest font-mono-plex text-white/70">{rep.role}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="font-heading font-black text-3xl leading-none">{total}</div>
          <div className="text-[9px] uppercase tracking-widest font-mono-plex text-white/70 mt-0.5">facilities</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-md bg-white/15 backdrop-blur-md px-2 py-1.5">
          <div className="text-[9px] uppercase tracking-widest text-white/70 font-mono-plex">Active</div>
          <div className="font-heading font-bold text-lg leading-tight">{rep.counts.active}</div>
        </div>
        <div className="rounded-md bg-white/15 backdrop-blur-md px-2 py-1.5">
          <div className="text-[9px] uppercase tracking-widest text-white/70 font-mono-plex">Progress</div>
          <div className="font-heading font-bold text-lg leading-tight">{rep.in_progress}</div>
        </div>
        <div className="rounded-md bg-white/15 backdrop-blur-md px-2 py-1.5">
          <div className="text-[9px] uppercase tracking-widest text-white/70 font-mono-plex">Inactive</div>
          <div className="font-heading font-bold text-lg leading-tight">{rep.counts.inactive}</div>
        </div>
      </div>
    </Link>
  );
}

export default function RepTiles({ reps }) {
  if (!reps || reps.length === 0) {
    return (
      <Card className="p-6 border-dashed border-slate-300 shadow-none text-sm text-slate-400" data-testid="rep-tiles-empty">
        No team members yet.
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="rep-tiles">
      {reps.map((r, i) => <RepTile key={r.user_id || "unassigned"} rep={r} index={i} />)}
    </div>
  );
}
